import { Link } from "react-router-dom";
import type { Film } from "@/types/film";

interface LatestSectionProps {
  films: Film[];
}

export default function LatestSection({ films }: LatestSectionProps) {
  const [lead, ...rest] = films;
  if (!lead) return null;

  return (
    <section className="w-full px-4 pb-4 md:px-10 md:pb-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="font-label text-[11px] uppercase tracking-[0.3em] text-accent-600">Latest</span>
          <h2 className="mt-2 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            最新伦理影视推荐
          </h2>
          <p className="mt-2 text-sm text-foreground-600">
            按上线时间整理，先看剧情，再看人物，最后读一读影评。
          </p>
        </div>
        <Link
          to="/movies"
          className="flex items-center gap-1.5 whitespace-nowrap text-sm text-primary-600 transition-colors hover:text-accent-600 cursor-pointer"
        >
          查看全部作品
          <i className="ri-arrow-right-line" />
        </Link>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <Link
          to={`/detail/${lead.id}`}
          className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 lg:col-span-6 cursor-pointer"
        >
          <div className="relative aspect-[16/10] w-full overflow-hidden bg-background-200">
            <img
              src={lead.backdrop}
              alt={`${lead.title}剧照`}
              title={`${lead.title} - 伦理视频最新推荐`}
              className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-primary-950/10 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex flex-wrap items-center gap-2 text-[11px] text-background-100">
              <span className="rounded-full bg-accent-500 px-2 py-0.5 text-background-50">最新上线</span>
              <span className="rounded-full border border-background-50/40 px-2 py-0.5">
                {lead.kind === "movie" ? "电影" : "剧集"}
              </span>
              <span>{lead.year}</span>
              <span>·</span>
              <span>{lead.region}</span>
            </div>
          </div>
          <div className="flex flex-1 flex-col p-5">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-heading text-lg font-semibold text-foreground-950 transition-colors group-hover:text-primary-600 md:text-xl">
                {lead.title}
              </h3>
              <span className="flex shrink-0 items-center gap-1 rounded-md bg-accent-100 px-2 py-1 text-xs font-medium text-accent-800">
                <i className="ri-star-fill text-[10px]" />
                {lead.rating.toFixed(1)}
              </span>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600 clamp-3">{lead.summary}</p>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-star-line" />
                {lead.director}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-movie-2-line" />
                {lead.genres.join(" / ")}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" />
                {lead.duration}
              </span>
            </div>
          </div>
        </Link>

        <div className="flex flex-col gap-4 lg:col-span-6">
          {rest.map((film) => (
            <Link
              key={film.id}
              to={`/detail/${film.id}`}
              className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 cursor-pointer"
            >
              <div className="h-[104px] w-[78px] shrink-0 overflow-hidden rounded-md bg-background-200">
                <img
                  src={film.cover}
                  alt={`${film.title}封面`}
                  title={`${film.title} - 伦理视频`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex min-w-0 flex-1 flex-col justify-center">
                <div className="flex items-center gap-2 text-[11px] text-foreground-500">
                  <span className="rounded-sm bg-secondary-100 px-1.5 py-0.5 text-secondary-900">
                    {film.kind === "movie" ? "电影" : `${film.episodes} 集`}
                  </span>
                  <span>
                    {film.year} · {film.region}
                  </span>
                  <span className="flex items-center gap-0.5 text-accent-700">
                    <i className="ri-star-fill text-[10px]" />
                    {film.rating.toFixed(1)}
                  </span>
                </div>
                <h3 className="mt-1.5 font-heading text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-600 truncate">
                  {film.title}
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-foreground-600 clamp-2">{film.summary}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}